<template>
    <div>
        <div class="content-header">
            <div class="container-fluid">
                <div class="row mb-2">
                    <div class="col-sm-6">
                        <h1 class="m-0 text-capitalize">{{ this.$route.name }}</h1>
                    </div>
                    <div class="col-sm-6">
                        <ol class="breadcrumb float-sm-right">
                            <li class="breadcrumb-item">
                                <router-link to="/">Home</router-link>
                            </li>
                            <li class="breadcrumb-item active text-capitalize"><i class="fas fa-list"></i> {{ this.$route.name }}
                            </li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="container-fluid">
                <data-table-wrapper
                    ref="dataTable"
                    :columns="columns"
                    :filters="filters"
                    :filter-defaults="filterDefaults"
                    :load-data-callback="loadDataCallback"
                    :show-filters="true"
                    disable-primary-column="true"
                    disable-edit-action="true"
                    disable-delete-action="true"
                    endpoint="/transactions-advanced"
                    api-service="analytics"
                    sort-column-default="roundStartedAt"
                    sort-order-default="DESC"
                >
                    <template v-slot:filter-buttons>
                        <button class="btn btn-default float-right" @click="doExport">
                            <span v-if="!exportInProgress">
                                <i class="fa fa-cloud-download"></i> {{ $t('Export') }}
                            </span>
                            <span v-else>
                                <i class="fas fa-spinner fa-pulse"></i> {{ $t('Export') }}
                            </span>
                        </button>
                    </template>
                </data-table-wrapper>
            </div>
        </section>

        <b-modal
            v-model="screenshotModalShow"
            :title="$t('Screenshot') + (screenshotRoundId ? ', round ' + screenshotRoundId : '')"
            size="xl"
            :lazy="true"
            hide-footer
        >
            <div v-if="screenshotLoading" class="text-center p-4">
                <i class="fas fa-spinner fa-pulse fa-2x"></i>
            </div>
            <div v-else-if="screenshotError" class="text-center p-4 text-muted">
                {{ $t(screenshotError) }}
            </div>
            <div v-else-if="screenshotUrl" class="text-center">
                <img
                    :src="screenshotUrl"
                    class="screenshot-image"
                    alt="Win screenshot"
                />
            </div>
        </b-modal>
    </div>
</template>

<script>
import * as formatUtils from '../helpers/formatUtils';
import * as dateUtils from '../helpers/dateUtils';
import DataTableWrapper from '../components/DataTableWrapper';
import {mapGetters} from 'vuex';

export default {
    components: {
        DataTableWrapper,
    },
    data() {
        let self = this;
        return {
            exportInProgress: false,
            screenshotModalShow: false,
            screenshotLoading: false,
            screenshotUrl: null,
            screenshotError: null,
            screenshotRoundId: null,
            filters: [
                {
                    field: 'fromDate',
                    placeholder: 'From Date',
                    label: 'From Date',
                    type: 'date-picker',
                    shortcuts: dateUtils.getFilterShortcuts()
                },
                {
                    field: 'toDate',
                    placeholder: 'To Date',
                    label: 'To Date',
                    type: 'date-picker',
                    shortcuts: dateUtils.getFilterShortcuts()
                },
                {
                    field: 'partnerIds',
                    placeholder: 'Select partners',
                    label: 'Partners',
                    type: 'multiselect',
                    optionLabel: 'name',
                    multiple: true,
                    getValue: (partners) => { return partners ? partners.map(partner => partner.id) : []; },
                    getOptions: () => this.getPartners,
                    trackBy: 'id'
                },
                {
                    field: 'operatorIds',
                    placeholder: 'Select operators',
                    label: 'Operators',
                    type: 'multiselect',
                    optionLabel: 'name',
                    multiple: true,
                    getValue: (operators) => { return operators ? operators.map(operator => operator.id) : []; },
                    getOptions: () => this.getOperatorsOptions,
                    trackBy: 'id'
                },
                {
                    field: 'gameIds',
                    placeholder: 'Select games',
                    label: 'Games',
                    type: 'multiselect',
                    optionLabel: 'name',
                    multiple: true,
                    getValue: (games) => { return games ? games.map(game => game.id) : []; },
                    getOptions: () => this.getGames,
                    trackBy: 'id'
                },
                {
                    field: 'uid',
                    placeholder: 'Player ID',
                    label: 'Player ID',
                },
                {
                    field: 'transactionId',
                    placeholder: 'Transaction ID',
                    label: 'Transaction ID',
                },
                {
                    field: 'roundId',
                    placeholder: 'Round ID',
                    label: 'Round ID',
                },
                {
                    field: 'betTypes',
                    placeholder: 'Select bet types',
                    label: 'Bet Types',
                    type: 'multiselect',
                    optionLabel: 'name',
                    multiple: true,
                    getValue: (types) => { return types ? types.map(type => type.id) : []; },
                    getOptions: () => [{id: 'debit', name: 'Debit'}, {id: 'tips', name: 'Tips'}, {id: 'credit', name: 'Credit'}, {id: 'rollback', name: 'Rollback'}],
                    trackBy: 'id'
                },
                {
                    field: 'currency',
                    placeholder: 'Currency',
                    label: 'Currency',
                },
                {
                    field: 'betAmountFrom',
                    placeholder: 'Bet Amount From',
                    label: 'Bet Amount From',
                    type: 'number',
                },
                {
                    field: 'betAmountTo',
                    placeholder: 'Bet Amount To',
                    label: 'Bet Amount To',
                    type: 'number',
                },
                {
                    field: 'winAmountFrom',
                    placeholder: 'Win Amount From',
                    label: 'Win Amount From',
                    type: 'number',
                },
                {
                    field: 'winAmountTo',
                    placeholder: 'Win Amount To',
                    label: 'Win Amount To',
                    type: 'number',
                },
                {
                    field: 'betDetails',
                    placeholder: 'Bet Details',
                    label: 'Bet Details',
                },
            ],
            columns: [
                {
                    field: 'transactionId',
                    title: 'Transaction ID',
                    sorting: false,
                },
                {
                    field: 'roundId',
                    title: 'Round ID',
                    sorting: false,
                },
                {
                    field: 'uid',
                    title: 'Player ID',
                    sorting: false,
                },
                {
                    field: 'betType',
                    title: 'Bet Type',
                    sorting: false,
                },
                {
                    field: 'roundStartedAt',
                    title: 'Round Start',
                    sorting: true,
                    isDate: true,
                },
                {
                    field: 'roundEndedAt',
                    title: 'Round End',
                    sorting: true,
                    isDate: true,
                },
                {
                    field: 'gameId',
                    title: 'Game',
                    sorting: false,
                    callback: ({row}) => {
                        const game = self.getGame(row.gameId);
                        return game ? game.name : String(row.gameId);
                    },
                },
                {
                    field: 'currency',
                    title: 'Currency',
                    sorting: false,
                },
                {
                    field: 'betAmount',
                    title: 'Bet Amount',
                    sorting: true,
                    callback: ({row}) => {
                        return (row.betAmount == 0 && row.betType == 'debit') ? '0 (freespin)' : formatUtils.formatNumberToFixed(row.betAmount);
                    },
                },
                {
                    field: 'winAmount',
                    title: 'Win Amount',
                    sorting: true,
                    callback: ({row}) => {
                        return formatUtils.formatNumberToFixed(row.winAmount);
                    },
                },
                {
                    field: 'betDetails',
                    title: 'Bet Details',
                    sorting: false,
                },
                {
                    field: 'screenshot',
                    title: 'Screenshot',
                    sorting: false,
                    callback: ({row}) => {
                        if (!row.hasScreenshot) {
                            return '';
                        }
                        return (
                            <span class="btn btn-outline-secondary btn-xs" onClick={() => self.showScreenshot(row)}>
                                <i class="fas fa-image"></i>&nbsp;{ self.$t('Show screenshot') }
                            </span>
                        );
                    },
                },
            ],
        };
    },
    computed: {
        ...mapGetters([
            'getGames',
            'getGame',
            'getPartners',
            'getPartner',
            'getOperators',
            'getOperator',
        ]),
        getOperatorsOptions() {
            return this.getOperators.filter((operator) => {
                let selectedPartnerIds = this.$refs.dataTable.getFilterValue('partnerIds');
                if (selectedPartnerIds.length === 0) {
                    return true;
                }

                if (!operator.partnerId) {
                    return false;
                }

                return selectedPartnerIds.includes(operator.partnerId);
            });
        },
        filterDefaults() {
            return {
                fromDate: dateUtils.getTodayDate()
            };
        }
    },
    methods: {
        loadDataCallback(table, data) {
            const items = (data.items || []).map(row => ({...row, hasScreenshot: false}));

            table.items = items;
            table.pagination.onPage = data.pagination.limit;
            table.pagination.total = data.pagination.totalCount;
            if (items.length === 0) {
                table.showEmpty = true;
            }

            const winRoundIds = [...new Set(items
                .filter(row => row.betType === 'credit' && Number(row.winAmount) > 0 && row.roundId)
                .map(row => String(row.roundId)))];
            if (winRoundIds.length === 0) {
                return;
            }

            const isWinRow = row => row.betType === 'credit' && Number(row.winAmount) > 0;

            this.$http({
                url: '/screenshots/check',
                method: 'POST',
                data: {roundIds: winRoundIds},
            }).then((resp) => {
                const existing = new Set((resp.data.existing || []).map(String));
                table.items = items.map(row => ({
                    ...row,
                    hasScreenshot: isWinRow(row) && existing.has(String(row.roundId)),
                }));
            }).catch(() => {
                // on check failure fall back to showing the button for all win rows
                table.items = items.map(row => ({
                    ...row,
                    hasScreenshot: isWinRow(row),
                }));
            });
        },
        showScreenshot(row) {
            this.screenshotRoundId = row.roundId;
            this.screenshotUrl = null;
            this.screenshotError = null;
            this.screenshotLoading = true;
            this.screenshotModalShow = true;

            this.$http({
                url: '/screenshots/' + row.roundId,
                method: 'GET',
            }).then((resp) => {
                this.screenshotUrl = resp.data.url;
            }).catch((err) => {
                this.screenshotError = err.response && err.response.status === 404
                    ? 'Screenshot not found'
                    : 'Failed to load screenshot';
            }).finally(() => {
                this.screenshotLoading = false;
            });
        },
        doExport() {
            if (this.exportInProgress) {
                return;
            }
            this.exportInProgress = true;
            this.$exportService.doExport('transactions-advanced', this.$refs.dataTable.getPageQuery()).finally(() => {
                this.exportInProgress = false;
            });
        },
    },
    mounted() {
    },
};
</script>
